import React, { useState } from "react";
import ImageLightbox from "./ImageLightbox";
import { Package, Layers, ImageOff } from "lucide-react";
import { getUploadUrl } from "@/lib/utils";

export interface ProductionThumbnailCellProps {
  imageUrl?: string | null;
  layoutUrl?: string | null;
  title?: string;
  subtitle?: string;
  type?: "product" | "layout" | "auto";
  size?: number; // default 40px
  className?: string;
}

export function ProductionThumbnailCell({
  imageUrl,
  layoutUrl,
  title,
  subtitle,
  type = "auto",
  size = 40,
  className = "",
}: ProductionThumbnailCellProps) {
  // Determine primary image URL:
  // For 'layout', prefer layoutUrl -> imageUrl
  // For 'product', prefer imageUrl -> layoutUrl
  // For 'auto', prefer layoutUrl (bài bình thực tế) -> imageUrl (sản phẩm)
  const rawUrl =
    type === "product"
      ? imageUrl || layoutUrl
      : layoutUrl || imageUrl;
  // Đường dẫn /uploads/... nằm trên máy chủ API, không phải máy chủ web
  const resolvedUrl = getUploadUrl(rawUrl);

  const resolvedAlt = title || (type === "layout" ? "Bài bình sản xuất" : "Hình ảnh sản phẩm");
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);

  if (!resolvedUrl || brokenUrl === resolvedUrl) {
    return (
      <div
        className={`flex items-center justify-center shrink-0 rounded-md border border-slate-200 bg-slate-50 text-slate-400 select-none shadow-xs ${className}`}
        style={{ width: `${size}px`, height: `${size}px` }}
        title={subtitle ? `${resolvedAlt} (${subtitle}) - Chưa có ảnh` : `${resolvedAlt} - Chưa có ảnh`}
        data-thumb-src={resolvedUrl || undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {type === "layout" ? (
          <Layers className="w-4 h-4 text-slate-400/80" />
        ) : (
          <Package className="w-4 h-4 text-slate-400/80" />
        )}
      </div>
    );
  }

  return (
    <div
      className={`shrink-0 inline-flex items-center justify-center relative group ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <ImageLightbox src={resolvedUrl} alt={resolvedAlt}>
        <div
          className="relative rounded-md overflow-hidden border border-slate-200 bg-white shadow-xs cursor-pointer hover:border-indigo-400 hover:ring-2 hover:ring-indigo-300/40 transition-all flex items-center justify-center"
          style={{ width: `${size}px`, height: `${size}px` }}
          title={subtitle ? `${resolvedAlt} (${subtitle}) - Click để phóng lớn` : `${resolvedAlt} - Click để phóng lớn`}
        >
          <img
            src={resolvedUrl}
            alt={resolvedAlt}
            className="w-full h-full object-cover transition-transform group-hover:scale-110"
            loading="lazy"
            onError={() => setBrokenUrl(resolvedUrl)}
          />
          {/* Subtle type indicator badge if it's a layout */}
          {layoutUrl && rawUrl === layoutUrl && (
            <span className="absolute bottom-0 right-0 bg-indigo-600/90 text-[8px] text-white font-bold px-1 rounded-tl leading-tight">
              Bình
            </span>
          )}
        </div>
      </ImageLightbox>
    </div>
  );
}

export default ProductionThumbnailCell;
